import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { getApiErrorLines } from "../utils/formErrors";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Archive, ArrowRight, Loader2, MapPin, Phone, Save } from "lucide-react";
import axiosInstance from "../utils/axiosInstance";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const emptyStorage = {
  storage_name: "",
  storage_address: "",
  storage_phone: "",
};

const Field = ({
  id,
  label,
  value,
  onChange,
  required,
  icon: Icon,
  children,
}) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-sm font-medium text-slate-700 text-right">
      {label}
      {required ? <span className="text-rose-500"> *</span> : null}
    </label>
    {children || (
      <div className="relative">
        {Icon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon size={18} />
          </span>
        ) : null}
        <input
          id={id}
          name={id}
          value={value || ""}
          onChange={onChange}
          required={required}
          className={cx(
            "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900",
            "placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary",
            Icon ? "pr-10" : "pr-3"
          )}
        />
      </div>
    )}
  </div>
);

const StorageForm = () => {
  const { storageId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(storageId);
  const [formData, setFormData] = useState(emptyStorage);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const title = useMemo(
    () => (isEdit ? "ویرایش انبار" : "انبار جدید"),
    [isEdit]
  );

  useEffect(() => {
    if (!isEdit) return;

    const fetchStorage = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await axiosInstance.get(`/storages/${storageId}/`);
        setFormData({
          storage_name: data.storage_name || "",
          storage_address: data.storage_address || "",
          storage_phone: data.storage_phone || "",
        });
      } catch (err) {
        setError("خطا در دریافت اطلاعات انبار");
      } finally {
        setLoading(false);
      }
    };

    fetchStorage();
  }, [isEdit, storageId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      if (isEdit) {
        await axiosInstance.put(`/storages/${storageId}/`, formData);
        navigate(`/storages/${storageId}`);
      } else {
        const { data } = await axiosInstance.post("/storages/", formData);
        navigate(`/storages/${data.id}`);
      }
    } catch (err) {
      const message = getApiErrorLines(err, { storage_name: "نام انبار" }, "خطا در ذخیره اطلاعات انبار").join(" ");
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-24 flex justify-center text-slate-600" dir="rtl">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="mt-24 px-3 sm:px-6 font-vazirmatn" dir="rtl">
      <div className="max-w-4xl mx-auto">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-lg overflow-hidden">
          <div className="px-5 sm:px-7 py-5 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Link
                to="/storages"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
              >
                <ArrowRight size={18} />
                بازگشت
              </Link>
              <div className="flex items-center justify-end gap-3 text-right">
                <div>
                  <h1 className="text-xl font-extrabold text-slate-900">
                    {title}
                  </h1>
                  <p className="mt-1 text-sm text-slate-500">
                    مشخصات محل نگهداری کالاهای خروج شده
                  </p>
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Archive size={22} />
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-5 sm:p-7 space-y-5">
            {error ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-right text-sm text-rose-700">
                {error}
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                id="storage_name"
                label="نام انبار"
                value={formData.storage_name}
                onChange={handleChange}
                required
                icon={Archive}
              />
              <Field
                id="storage_phone"
                label="تلفن انبار"
                value={formData.storage_phone}
                onChange={handleChange}
                icon={Phone}
              />
              <Field id="storage_address" label="آدرس انبار">
                <div className="relative">
                  <span className="absolute right-3 top-3 text-slate-400">
                    <MapPin size={18} />
                  </span>
                  <textarea
                    id="storage_address"
                    name="storage_address"
                    value={formData.storage_address || ""}
                    onChange={handleChange}
                    rows={4}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                  />
                </div>
              </Field>
            </div>

            <div className="flex justify-end border-t border-slate-200 pt-5">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex h-11 min-w-[150px] items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-bold text-white shadow-sm hover:opacity-95 disabled:opacity-60"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                ذخیره
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StorageForm;
